import type { KnockGuideFilterParams, KnockGuideStep } from "@knocklabs/client";
import { useGuides } from "@knocklabs/react";
import { type SxProps, type Theme } from "@mui/material";
import { useOpenUrl } from "@src/mobile/utils/openUrl";
import { useHistory } from "react-router-dom";

import { useKnockMarkAsSeen } from "../hooks/useKnockMarkAsSeen";
import { handleGuideAction } from "../lib/handleGuideAction";
import { normalizeButtonContent } from "../lib/normalizeButtonContent";
import { type GuideButtonAction, type GuideButtonContent } from "../types";
import { KnockErrorBoundary } from "./KnockErrorBoundary";
import { type KnockGuidesModalContent, KnockGuidesModalView } from "./KnockGuidesModalView";

interface KnockModalContent {
  body: string;
  dismissible?: boolean;
  image?: {
    url: string;
    alt?: string;
    action?: string;
  };
  /* eslint-disable @typescript-eslint/naming-convention */
  primary_button?: GuideButtonContent;
  secondary_button?: GuideButtonContent;
  /* eslint-enable @typescript-eslint/naming-convention */
  title: string;
}

function normalizeContent(content?: KnockModalContent): KnockGuidesModalContent | undefined {
  const title = content?.title?.trim();
  const body = content?.body?.trim();
  if (!title || !body) {
    return undefined;
  }

  return {
    title,
    body,
    primaryButton: normalizeButtonContent(content?.primary_button),
    secondaryButton: normalizeButtonContent(content?.secondary_button),
    dismissible: content?.dismissible ?? true,
    image: content?.image?.url ? { url: content.image.url, alt: content.image.alt } : undefined,
  };
}

export interface KnockGuidesModalProps {
  filter: KnockGuideFilterParams;
  primary?: GuideButtonAction;
  secondary?: GuideButtonAction;
  sx?: SxProps<Theme>;
}

interface ModalItemProps {
  content: KnockGuidesModalContent;
  guideKey: string;
  primary?: GuideButtonAction;
  secondary?: GuideButtonAction;
  step: KnockGuideStep;
  sx?: SxProps<Theme>;
}

function ModalItem(props: ModalItemProps) {
  const { content, guideKey, primary, secondary, step, sx } = props;

  const history = useHistory();
  const openUrl = useOpenUrl();
  useKnockMarkAsSeen(step);

  const actionData = {
    key: guideKey,
    navigate: (path: string) => history.push(path),
    openUrl,
    step,
  };

  return (
    <KnockGuidesModalView
      content={content}
      sx={sx}
      onPrimaryClick={
        content.primaryButton
          ? async () => {
              await handleGuideAction({
                ...actionData,
                action: primary,
                content: content.primaryButton!,
              });
            }
          : undefined
      }
      onSecondaryClick={
        content.secondaryButton
          ? async () => {
              await handleGuideAction({
                ...actionData,
                action: secondary,
                content: content.secondaryButton!,
              });
            }
          : undefined
      }
      onDismiss={() => {
        step.markAsArchived();
      }}
    />
  );
}

function KnockGuidesModalInner(props: KnockGuidesModalProps) {
  const { filter, primary, secondary, sx } = props;
  const { guides } = useGuides(filter);

  if (guides.length === 0) {
    return null;
  }

  // Only show one modal at a time — the first matching guide.
  const guide = guides[0];
  const step = guide?.getStep();
  const content = normalizeContent(step?.content as KnockModalContent | undefined);

  if (!guide || !step || !content) {
    return null;
  }

  return (
    <ModalItem
      content={content}
      guideKey={guide.key}
      primary={primary}
      secondary={secondary}
      step={step}
      sx={sx}
    />
  );
}

/**
 * Connected Knock guide modal that fetches guides by filter, normalizes
 * Knock content, and renders `KnockGuidesModalView` for the first matching guide.
 *
 * Only one modal is shown at a time to avoid stacking dialogs.
 * Wrap in a `KnockErrorBoundary` so any Knock SDK errors don't crash the host page.
 */
export function KnockGuidesModal(props: KnockGuidesModalProps) {
  return (
    <KnockErrorBoundary>
      <KnockGuidesModalInner {...props} />
    </KnockErrorBoundary>
  );
}
